/* eslint-disable no-nested-ternary */
import { observer } from 'mobx-react-lite';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../stores/store';
import { TextBody } from '../../../common/typography/typography.styles';
import {
  getFormulaDivider,
  getLengthConfig,
  getWeightConfig,
} from './dimensionsConfig';
import FormFieldText from '../../../common/form-field-text/form-field-text.component';
import {
  Messages,
  VolumetricWeightWarningText,
} from './parcel-details-dimensions.styles';
import Tip from '../../../common/toasts/tip.component';
import ChangeHandler from './change-handler.component';
import { FlexContainer } from '../../../common/flex-container/flex-container.styles';
import useVolumetricWeight from './use-volumetric-weight';
import useDimensionsValidationSchema from './use-dimensions-validation-schema';
import { HidableContainer } from '../../../common/input/input.styles';

export interface DimensionsFormValues {
  weight: number | undefined;
  size_x: number | undefined;
  size_y: number | undefined;
  size_z: number | undefined;
}

interface Props {
  isHidden?: boolean;
}

const ParcelDimensionsForm = ({ isHidden }: Props) => {
  const {
    userStore: { measures },
    parcelCreationStore: {
      deliveryLimitations,
      parcelDimensions,
      isNextButtonClicked,
      prefillInProgress,
    },
    shipmentCountriesStore: { isDirectionsListApiLoading },
  } = useStore();
  const weightConfig = getWeightConfig(measures);
  const lengthConfig = getLengthConfig(measures);

  const { t } = useTranslation();

  const initialValues: DimensionsFormValues = {
    weight: parcelDimensions?.weight,
    size_x: parcelDimensions?.size_x,
    size_y: parcelDimensions?.size_y,
    size_z: parcelDimensions?.size_z,
  };

  const validationSchema = useDimensionsValidationSchema({
    deliveryLimitations,
    measures,
  });

  const {
    isVolumetricWeightTooLarge,
    shouldShowVolumetricError,
    shouldShowVolWeight,
    volWeightNumber,
    volumetricWeight,
  } = useVolumetricWeight({
    deliveryLimitations,
    isOnlyWeightValidation: false,
    measures,
    parcelDimensions,
  });

  const inputsDisabled = !deliveryLimitations;

  return (
    <HidableContainer $isHidden={isHidden}>
      <Formik
        onSubmit={() => {}}
        enableReinitialize={false}
        validationSchema={validationSchema}
        initialValues={initialValues}>
        {() => {
          const commonProps = {
            validationMethod: 'on-type',
            inputMode: 'decimal',
            showSkeleton: prefillInProgress || isDirectionsListApiLoading,
            disabled: inputsDisabled,
          } as const;

          const sideProps = {
            ...commonProps,
            infoText: lengthConfig.translation,
          };
          return (
            <form
              aria-labelledby='parcel-dimensions-heading'
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
              }}>
              <FormFieldText
                label='weight'
                name='weight'
                infoText={weightConfig.translation}
                {...commonProps}
                triggerValidation={isNextButtonClicked.value}
              />

              <FormFieldText
                label='length'
                name='size_x'
                {...sideProps}
                triggerValidation={isNextButtonClicked.value}
              />

              <FormFieldText
                $mb='0'
                label='width'
                name='size_y'
                {...sideProps}
                triggerValidation={isNextButtonClicked.value}
              />

              <FormFieldText
                $mb='0'
                label='height'
                name='size_z'
                {...sideProps}
                triggerValidation={isNextButtonClicked.value}
              />
              <ChangeHandler />
            </form>
          );
        }}
      </Formik>
      {(shouldShowVolWeight || shouldShowVolumetricError) && (
        <Messages>
          <div>
            {shouldShowVolWeight && (
              <FlexContainer
                $justify='start'
                $gap='0.4rem'>
                <TextBody>{t('create_parcel_volumetric_calculated')} </TextBody>

                <TextBody $fontWeight={700}>
                  {volumetricWeight === 0
                    ? 0
                    : volumetricWeight > 0.01
                      ? volWeightNumber
                      : '< 0.01'}{' '}
                  {weightConfig.translation}
                </TextBody>
              </FlexContainer>
            )}
            {shouldShowVolumetricError && (
              <VolumetricWeightWarningText>
                {t('create_parcel_volumetric_large')}{' '}
                {`(${t('length')}×${t('width')}×${t('height')})/${getFormulaDivider(measures)}`}{' '}
                {'<'} {deliveryLimitations?.maxWeight}
              </VolumetricWeightWarningText>
            )}
          </div>
          {isVolumetricWeightTooLarge && !shouldShowVolumetricError && (
            <Tip
              appearance='warning'
              title={t('create_parcel_volumetric_tip')}
            />
          )}
        </Messages>
      )}
    </HidableContainer>
  );
};

export default observer(ParcelDimensionsForm);
